import { useState } from 'react';
import { toast } from 'sonner';
import { Search, UserPlus, UserMinus, Users } from 'lucide-react';
import { ModalShell } from '../ui/ModalShell';
import { Button } from '../ui/Button';
import { Pill } from '../ui/Pill';
import { SkeletonList } from '../ui/Skeleton';
import { ApiError } from '../../lib/api';
import {
  useAttachAttendee,
  useAttendeeCandidates,
  useDetachAttendee,
  useMobilisationAttendees,
} from '../../hooks/useMobilisationAttendees';

export interface MobilisationAttendeesModalProps {
  /** null = closed. */
  mobilisation: { id: string; name: string } | null;
  onClose: () => void;
}

/**
 * Membership panel for one mobilisation: who attends, search-to-add
 * anyone not yet in it, remove mis-allocations. Removing someone who
 * already has events in the mobilisation is refused by the API (409)
 * so the audit chain can never be orphaned; the error is surfaced
 * as-is.
 */
export function MobilisationAttendeesModal({
  mobilisation,
  onClose,
}: MobilisationAttendeesModalProps) {
  const titleId = 'mobilisation-attendees-title';
  const [q, setQ] = useState('');
  const id = mobilisation?.id ?? null;
  const attendees = useMobilisationAttendees(id);
  const candidates = useAttendeeCandidates(id, q);
  const attach = useAttachAttendee(id ?? '');
  const detach = useDetachAttendee(id ?? '');

  function close() {
    setQ('');
    onClose();
  }

  async function onAttach(person_id: string, name: string) {
    try {
      await attach.mutateAsync(person_id);
      toast.success(`Added ${name}`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'add failed');
    }
  }

  async function onDetach(person_id: string, name: string) {
    try {
      await detach.mutateAsync(person_id);
      toast.success(`Removed ${name}`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'remove failed');
    }
  }

  return (
    <ModalShell
      open={!!mobilisation}
      onClose={close}
      titleId={titleId}
      maxWidth="max-w-lg"
    >
      <div className="p-5">
        <h2 id={titleId} className="flex items-center gap-2 text-base font-semibold">
          <Users className="h-4 w-4 text-accent" />
          Attendees, {mobilisation?.name}
        </h2>
        <p className="mt-1 text-sm text-fg-muted">
          People in this mobilisation. Anyone not listed here is invisible
          to its reception and station staff.
        </p>

        <h3 className="mt-4 text-xs font-semibold uppercase tracking-wider text-fg-muted">
          Add people
        </h3>
        <label className="relative mt-2 block">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-dim" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter by name..."
            className="block h-10 w-full rounded-md border border-line-strong/20 bg-surface pl-9 pr-3 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
          />
        </label>
        <ul className="mt-2 max-h-56 divide-y divide-divider/10 overflow-y-auto rounded-md border border-line/10 bg-surface-2">
          {candidates.isLoading ? (
            <li className="px-3 py-2 text-sm text-fg-muted">Loading people...</li>
          ) : (candidates.data ?? []).length === 0 ? (
            <li className="px-3 py-2 text-sm text-fg-muted">
              {q.trim()
                ? "No one matches who isn't already an attendee."
                : 'Everyone is already in this mobilisation.'}
            </li>
          ) : (
            (candidates.data ?? []).map((p) => (
              <li key={p.id} className="flex items-center gap-3 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{p.full_name}</div>
                  <div className="truncate text-xs text-fg-muted">{p.email}</div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  iconLeft={<UserPlus className="h-3.5 w-3.5" />}
                  onClick={() => onAttach(p.id, p.full_name)}
                  disabled={attach.isPending}
                >
                  Add
                </Button>
              </li>
            ))
          )}
        </ul>

        <h3 className="mt-5 text-xs font-semibold uppercase tracking-wider text-fg-muted">
          Attendees ({attendees.data?.length ?? 0})
        </h3>
        <div className="mt-2 max-h-72 overflow-y-auto rounded-md border border-line/10">
          {attendees.isLoading ? (
            <SkeletonList rows={3} />
          ) : (attendees.data ?? []).length === 0 ? (
            <p className="px-3 py-4 text-sm text-fg-muted">
              No attendees yet. People created or imported while scoped to
              this mobilisation land here automatically, or add them above.
            </p>
          ) : (
            <ul className="divide-y divide-divider/10">
              {(attendees.data ?? []).map((p) => (
                <li key={p.id} className="flex items-center gap-3 px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{p.full_name}</div>
                    <div className="truncate text-xs text-fg-muted">{p.email}</div>
                  </div>
                  {p.arrived ? <Pill tone="success" size="sm">on site</Pill> : null}
                  <Button
                    variant="ghost"
                    size="sm"
                    iconLeft={<UserMinus className="h-3.5 w-3.5" />}
                    onClick={() => onDetach(p.id, p.full_name)}
                    disabled={detach.isPending}
                    className="text-danger-soft-fg hover:bg-danger-soft/15"
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </ModalShell>
  );
}
